import React, { useEffect, useState } from 'react';
import { Upload, message } from 'antd';
import { PhotoIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { TWModal, TWButton, TWInput, FieldLabel } from '../../../components/ui';
import imageLibraryAdminApi from '../../../services/imageLibraryAdminApi';
import imageLibraryApi from '../../../services/imageLibraryApi';
import { validateUploadFile, MB } from '../../../utils/fileValidation';

const ACCEPT = 'image/*';
const MAX_BYTES = 10 * MB;

const textareaCls =
  'block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 transition focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder-slate-500';

// image: null -> oluşturma; obje -> düzenleme. Düzenlemede yeni dosya opsiyonel.
const ImageLibraryUpsertModal = ({ open, image, onClose, onSaved }) => {
  const isEdit = !!image?.id;
  const [form, setForm] = useState({
    name: '',
    description: '',
    folder: '',
    tags: '',
  });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [folderOptions, setFolderOptions] = useState([]);

  useEffect(() => {
    if (!open) return;
    setForm({
      name: image?.name || '',
      description: image?.description || '',
      folder: image?.folder || '',
      tags: Array.isArray(image?.tags) ? image.tags.join(', ') : '',
    });
    setFile(null);
    setPreview(image?.imagepath || '');
    setError('');
    setSaving(false);
  }, [open, image]);

  // Mevcut klasörleri datalist için yükle (yazım hatalarını azaltır).
  useEffect(() => {
    if (!open) return;
    imageLibraryApi
      .folders()
      .then((res) =>
        setFolderOptions(
          (res.data?.data || []).map((f) => f.folder).filter(Boolean),
        ),
      )
      .catch(() => setFolderOptions([]));
  }, [open]);

  const setField = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const acceptFile = (picked) => {
    setFile(picked);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(picked);
  };

  // Gate every file (drag-drop or click pick) through the same validation.
  // Always return a falsy/IGNORE value so antd never auto-uploads.
  const beforeUpload = (picked) => {
    const result = validateUploadFile(picked, { accept: ACCEPT, maxBytes: MAX_BYTES });
    if (!result.ok) {
      message.error(
        result.reason === 'size'
          ? 'Image is too large. Max 10 MB.'
          : 'Unsupported file. Please choose an image.',
      );
      return Upload.LIST_IGNORE;
    }
    acceptFile(picked);
    return false;
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Name is required.');
      return;
    }
    if (!isEdit && !file) {
      setError('An image file is required.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      const fd = new FormData();
      if (isEdit) fd.append('id', image.id);
      fd.append('name', form.name.trim());
      fd.append('description', form.description);
      fd.append('folder', form.folder);
      fd.append('tags', form.tags);
      if (file) fd.append('image', file);
      await imageLibraryAdminApi.upsert(fd);
      message.success(isEdit ? 'Image updated' : 'Image added');
      onSaved();
      onClose();
    } catch (err) {
      const apiMessage =
        typeof err?.response?.data === 'string'
          ? err.response.data
          : err?.response?.data?.message || 'Failed to save image';
      message.error(apiMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <TWModal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit library image' : 'Add library image'}
      description="Photos here are shared across every restaurant's menu."
      size="2xl"
      showCloseButton
      footer={
        <div className="flex justify-end gap-2">
          <TWButton label="Cancel" disabled={saving} onClick={onClose} />
          <TWButton
            variant="primary"
            label={saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add image'}
            loading={saving}
            onClick={handleSave}
          />
        </div>
      }
    >
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-[minmax(0,12rem)_1fr]">
        <div>
          <FieldLabel>Image</FieldLabel>
          <Upload.Dragger
            accept={ACCEPT}
            multiple={false}
            maxCount={1}
            showUploadList={false}
            beforeUpload={beforeUpload}
            disabled={saving}
            className="image-library-dragger"
          >
            <div className="group relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-lg">
              {preview ? (
                <>
                  <img
                    src={preview}
                    alt="preview"
                    className="h-full w-full object-cover"
                  />
                  <span className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1 bg-gradient-to-t from-black/80 to-transparent pb-2 pt-6 text-[0.65rem] font-semibold text-white opacity-0 transition group-hover:opacity-100">
                    <ArrowUpTrayIcon className="h-3.5 w-3.5" />
                    Replace
                  </span>
                </>
              ) : (
                <span className="flex flex-col items-center gap-2 text-slate-400 transition group-hover:text-blue-500 dark:text-slate-500 dark:group-hover:text-sky-400">
                  <PhotoIcon className="h-9 w-9" />
                  <span className="text-xs font-semibold">Click or drag image</span>
                </span>
              )}
            </div>
          </Upload.Dragger>
          {isEdit ? (
            <p className="mt-1.5 text-xs leading-snug text-slate-400 dark:text-slate-500">
              Leave unchanged to keep the current image. A new image
              overwrites it for every linked product.
            </p>
          ) : null}
        </div>

        <div className="space-y-4">
          <TWInput
            label="Name"
            required
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
            placeholder="Coca-Cola Can 330ml"
          />

          <div>
            <FieldLabel>Folder</FieldLabel>
            <TWInput
              value={form.folder}
              onChange={(e) => setField('folder', e.target.value)}
              placeholder="Beverages"
              list="image-library-folders"
            />
            <datalist id="image-library-folders">
              {folderOptions.map((f) => (
                <option key={f} value={f} />
              ))}
            </datalist>
          </div>

          <div>
            <FieldLabel>Tags</FieldLabel>
            <TWInput
              value={form.tags}
              onChange={(e) => setField('tags', e.target.value)}
              placeholder="beverage, can, coca, cola"
            />
            <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
              Comma-separated. Replaces the existing tag set.
            </p>
          </div>

          <div>
            <FieldLabel>Description</FieldLabel>
            <textarea
              rows={2}
              className={textareaCls}
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
              placeholder="Internal notes"
            />
          </div>

          {error ? (
            <p className="text-xs font-medium text-rose-600 dark:text-rose-400">
              {error}
            </p>
          ) : null}
        </div>
      </div>
    </TWModal>
  );
};

export default ImageLibraryUpsertModal;
